import { ThunkAction } from '@reduxjs/toolkit';
import { AnyAction } from 'redux';
import { RootState } from '../index';
import {
  loginStart,
  loginSuccess,
  loginFailure,
  logout as logoutAction,
  mfaChallenge,
  mfaSetupNeeded,
  mfaClearTemp,
} from '../slices/authSlice';
import { authAPI, SendOTPRequest, VerifyOTPRequest } from '@/services/authAPI';
import { mfaAPI } from '@/services/mfaAPI';
import { OTP_TYPES } from '@/services/otpAuthService';
import { User, UserRole, UserStatus, VerificationStatus } from '@/types';
import { MFA_CHALLENGE_KEY } from '@/services/mfaAPI';

type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, AnyAction>;

// Helper to transform raw backend user data into the frontend User type
const transformUserData = (raw: any): User => ({
  id: raw.id,
  email: raw.email || '',
  fullName: raw.fullName || raw.name || 'Unknown User',
  phone: raw.phone || undefined,
  role: (raw.role as UserRole) || 'customer',
  permissions: raw.permissions || [], // Preserve permissions from backend
  status: (raw.status as UserStatus) || 'active',
  emailVerified: raw.emailVerified || false,
  phoneVerified: raw.phoneVerified || false,
  isVerified: raw.isVerified || false,
  canRentCars: raw.canRentCars || false,
  canHireDrivers: raw.canHireDrivers || false,
  verificationStatus: (raw.verificationStatus as VerificationStatus) || VerificationStatus.NONE,
  verificationId: raw.verificationId,
  profileCompletionPercentage: raw.profileCompletionPercentage || 0,
  documentsSubmitted: raw.documentsSubmitted || false,
  verificationSubmittedAt: raw.verificationSubmittedAt,
  verificationApprovedAt: raw.verificationApprovedAt,
  onboardingCompleted: raw.onboardingCompleted || false,
  createdAt: raw.createdAt || new Date().toISOString(),
  updatedAt: raw.updatedAt || new Date().toISOString(),
});

export const login = (emailOrPhone: string, password: string): AppThunk<Promise<any>> => async (dispatch) => {
  try {
    dispatch(loginStart());

    const isEmail = emailOrPhone.includes('@');
    const loginPayload = isEmail
      ? { email: emailOrPhone, password }
      : { phone: emailOrPhone, password };

    const response = await authAPI.login(loginPayload);

    // Check if MFA is required (mandatory for all admins)
    if ((response as any).requires2FA) {
      const mfaResponse = response as any;
      // Needed by every MFA step (see mfaAPI); valid for 10 minutes
      if (mfaResponse.mfaToken) {
        sessionStorage.setItem(MFA_CHALLENGE_KEY, mfaResponse.mfaToken);
      }
      if (mfaResponse.requires2FASetup) {
        // User needs to set up MFA for the first time
        dispatch(mfaSetupNeeded({ userId: mfaResponse.userId }));
        return { mfaSetupRequired: true, userId: mfaResponse.userId };
      }
      // User has MFA enabled, needs to verify
      dispatch(mfaChallenge({
        userId: mfaResponse.userId,
        mfaMethod: mfaResponse.mfaMethod || 'totp',
        phoneHint: mfaResponse.phoneHint,
      }));
      return { mfaRequired: true, mfaMethod: mfaResponse.mfaMethod };
    }

    // No MFA — standard login
    // Store tokens first
    sessionStorage.removeItem(MFA_CHALLENGE_KEY);
    localStorage.setItem('token', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);

    // CRITICAL FIX: Fetch fresh profile to get latest permissions from database
    // Login endpoint may not return permissions or may have cached/stale data
    const profileResponse = await authAPI.getCurrentProfile();

    const user = profileResponse.success && profileResponse.user
      ? transformUserData(profileResponse.user)
      : transformUserData(response.user); // Fallback to login response if profile fetch fails

    dispatch(loginSuccess({
      user,
      token: response.accessToken,
      refreshToken: response.refreshToken,
    }));

    return response;
  } catch (error: any) {
    dispatch(loginFailure(error.message || 'Login failed'));
    throw error;
  }
};

// MFA verification during login (TOTP or SMS)
export const verifyMFALogin = (code: string, userId: string, method: 'totp' | 'sms'): AppThunk<Promise<any>> => async (dispatch) => {
  try {
    dispatch(loginStart());

    const response = method === 'sms'
      ? await mfaAPI.verifySMS(userId, code)
      : await mfaAPI.verifyTOTP(userId, code);

    if (!response.success) {
      throw new Error('MFA verification failed');
    }

    // Store tokens first
    sessionStorage.removeItem(MFA_CHALLENGE_KEY);
    localStorage.setItem('token', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);

    // Fetch fresh profile to get latest permissions
    const profileResponse = await authAPI.getCurrentProfile();

    const user = profileResponse.success && profileResponse.user
      ? transformUserData(profileResponse.user)
      : transformUserData(response.user);

    dispatch(mfaClearTemp());
    dispatch(loginSuccess({
      user,
      token: response.accessToken,
      refreshToken: response.refreshToken,
    }));

    return response;
  } catch (error: any) {
    dispatch(loginFailure(error.message || 'MFA verification failed'));
    throw error;
  }
};

// Generate TOTP secret + QR code for setup
export const generateTOTPSetup = (userId: string): AppThunk<Promise<any>> => async (_dispatch) => {
  return mfaAPI.generateTOTP(userId);
};

// Check if admin has a phone number on file
export const checkPhoneOnFile = (userId: string): AppThunk<Promise<any>> => async (_dispatch) => {
  return mfaAPI.checkPhoneOnFile(userId);
};

// Setup SMS MFA — send code to phone on file
export const setupSMSMfa = (userId: string): AppThunk<Promise<any>> => async (_dispatch) => {
  return mfaAPI.setupSMS(userId);
};

// Verify TOTP setup and enable 2FA
export const verifyTOTPSetup = (userId: string, secret: string, code: string): AppThunk<Promise<any>> => async (dispatch) => {
  try {
    dispatch(loginStart());

    const response = await mfaAPI.verifyTOTPSetup(userId, secret, code);

    if (response.user && response.accessToken) {
      // Store tokens first
      sessionStorage.removeItem(MFA_CHALLENGE_KEY);
      localStorage.setItem('token', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);

      // Fetch fresh profile to get latest permissions
      const profileResponse = await authAPI.getCurrentProfile();

      const user = profileResponse.success && profileResponse.user
        ? transformUserData(profileResponse.user)
        : transformUserData(response.user);

      dispatch(mfaClearTemp());
      dispatch(loginSuccess({
        user,
        token: response.accessToken,
        refreshToken: response.refreshToken,
      }));
    }

    return response;
  } catch (error: any) {
    dispatch(loginFailure(error.message || 'TOTP setup verification failed'));
    throw error;
  }
};

// Verify SMS setup and enable MFA
export const verifySMSSetup = (userId: string, code: string): AppThunk<Promise<any>> => async (dispatch) => {
  try {
    dispatch(loginStart());

    const response = await mfaAPI.verifySMSSetup(userId, code);

    if (response.user && response.accessToken) {
      // Store tokens first
      sessionStorage.removeItem(MFA_CHALLENGE_KEY);
      localStorage.setItem('token', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);

      // Fetch fresh profile to get latest permissions
      const profileResponse = await authAPI.getCurrentProfile();

      const user = profileResponse.success && profileResponse.user
        ? transformUserData(profileResponse.user)
        : transformUserData(response.user);

      dispatch(mfaClearTemp());
      dispatch(loginSuccess({
        user,
        token: response.accessToken,
        refreshToken: response.refreshToken,
      }));
    }

    return response;
  } catch (error: any) {
    dispatch(loginFailure(error.message || 'SMS setup verification failed'));
    throw error;
  }
};

export const logout = (): AppThunk => async (dispatch) => {
  try {
    await authAPI.logout();
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Enhanced logout - clear all stored credentials and sensitive data
    clearStoredCredentials();
    dispatch(logoutAction());
  }
};

// Security enhancement: Clear all stored passwords and sensitive data
const clearStoredCredentials = () => {
  try {
    // Clear any stored passwords (if using any password managers or form data)
    const forms = document.querySelectorAll('form');
    forms.forEach(form => {
      const passwordInputs = form.querySelectorAll('input[type="password"]');
      passwordInputs.forEach((input: any) => {
        input.value = '';
      });
    });

    // Clear any auto-saved form data from browser
    if ('serviceWorker' in navigator) {
      // Clear any cached credentials from service workers
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          // Post message to service worker to clear cached credentials
          registration.active?.postMessage({ type: 'CLEAR_CREDENTIALS' });
        });
      });
    }

    // Clear browser autocomplete data for sensitive fields
    localStorage.removeItem('lastEmail');
    localStorage.removeItem('lastPhone');
    localStorage.removeItem('rememberMe');
    
  } catch (error) {
    console.error('Error clearing stored credentials:', error);
  }
};

export const checkAuth = (): AppThunk => async (dispatch) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      dispatch(logoutAction());
      return;
    }
    
    const user = await authAPI.getCurrentUser();
    const transformedUser = transformUserData(user);

    dispatch(loginSuccess({
      user: transformedUser,
      token: localStorage.getItem('token')!,
      refreshToken: localStorage.getItem('refreshToken')!,
    }));
  } catch (error) {
    dispatch(logoutAction());
  }
};

// 🔧 FIXED: OTP Authentication Thunks with consistent type parameters

export const sendOTP = (request: SendOTPRequest): AppThunk => async (_dispatch) => {
  try {
    
    const response = await authAPI.sendOTP(request);
    
    return response;
  } catch (error: any) {
    console.error('❌ Thunk: Send OTP failed:', error);
    throw error;
  }
};

export const verifyOTP = (request: VerifyOTPRequest): AppThunk => async (dispatch) => {
  try {
    dispatch(loginStart());
    
    
    const response = await authAPI.verifyOTP(request);
    
    if (response.data?.user && response.data?.token) {
      const user = transformUserData({
        ...response.data.user,
        phone: response.data.user.phone || request.phone,
        phoneVerified: true,
      });
      
      // Store tokens
      localStorage.setItem('token', response.data.token);
      if (response.data.refreshToken) {
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }
      
      dispatch(loginSuccess({
        user,
        token: response.data.token,
        refreshToken: response.data.refreshToken || '',
      }));
      
    }
    
    return response;
  } catch (error: any) {
    console.error('❌ Thunk: Verify OTP failed:', error);
    dispatch(loginFailure(error.message || 'OTP verification failed'));
    throw error;
  }
};

// 🎯 High-level OTP authentication flows
export const loginWithOTP = (phone: string): AppThunk => async (dispatch) => {
  return dispatch(sendOTP({ phone, type: OTP_TYPES.LOGIN }));
};

export const verifyLoginOTP = (phone: string, otp: string): AppThunk => async (dispatch) => {
  return dispatch(verifyOTP({ phone, otp, type: OTP_TYPES.LOGIN }));
};

export const registerWithOTP = (phone: string): AppThunk => async (dispatch) => {
  return dispatch(sendOTP({ phone, type: OTP_TYPES.REGISTRATION }));
};

export const verifyRegistrationOTP = (phone: string, otp: string): AppThunk => async (dispatch) => {
  return dispatch(verifyOTP({ phone, otp, type: OTP_TYPES.REGISTRATION }));
};

export const resetPasswordOTP = (phone: string): AppThunk => async (dispatch) => {
  return dispatch(sendOTP({ phone, type: OTP_TYPES.PASSWORD_RESET }));
};

export const verifyPasswordResetOTP = (phone: string, otp: string): AppThunk => async (dispatch) => {
  return dispatch(verifyOTP({ phone, otp, type: OTP_TYPES.PASSWORD_RESET }));
};